import React from 'react';
import { useTranslation } from 'react-i18next';

// Types
import YesNoQuestion from '../../../ui/advisory-flow/components/shared-components/YesNoQuestion';
import { Address } from '../../../../../../../types/Common';
import { useParams } from 'react-router';
import { ProductFactoryGroup } from '../../../../../../../types/ProductFactory';
import { useFlowsSelector } from '../../../../../../common/src/utils/globalVariables';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import useMobileBreakpoints from '../../../../../../common/src/hooks/useMobileBreakpoints';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import { formatAddress } from '../../../../../../common/src/utils/commonUtils';

interface Props {
  value?: string;
  onChange: (arg0?: Address) => void;
  nextQuestionHandler: () => void;
  previousQuestionHandler: () => void;
  nextSubView: () => void;
  withGreeting?: boolean;
}

const ReuseAddress: React.FC<React.PropsWithChildren<Props>> = ({
  onChange,
  nextSubView,
  nextQuestionHandler,
  previousQuestionHandler,
}) => {
  const { t } = useTranslation();
  const { position } = useParams<{
    position: string;
  }>();

  const { isLaptop } = useMobileBreakpoints();

  const answers = useFlowsSelector(
    (state) => state.flows.answers,
    (prevAnswers, nextAnswers) => {
      const prevAddress = prevAnswers.find(
        ({ id }) => id === 'policy_holder.address',
      )?.answer;
      const nextAddress = nextAnswers.find(
        ({ id }) => id === 'policy_holder.address',
      )?.answer;
      return prevAddress === nextAddress;
    },
  );
  const questionnaire = useFlowsSelector(
    (state) => state.flows.questionnaire,
    (prev, next) => {
      const prevGroup = prev.groups[position] as ProductFactoryGroup;
      const nextGroup = next.groups[position] as ProductFactoryGroup;
      return (
        (prevGroup.duplicateQuestionData &&
          prevGroup.duplicateQuestionData?.amount > 1) ===
        (nextGroup.duplicateQuestionData &&
          nextGroup.duplicateQuestionData?.amount > 1)
      );
    },
  );
  const currentGroup = questionnaire.groups[position] as ProductFactoryGroup;

  const policyHolderAdress = answers.find(
    ({ id }) => id === 'policy_holder.address',
  )?.answer as Address | undefined;

  const handleYes = () => {
    onChange(policyHolderAdress as Address);
    nextQuestionHandler();
  };

  const handleNo = () => {
    onChange(undefined);
    nextSubView();
  };

  return (
    <AdvisoryFlowMainContainer>
      <QuestionGroupAssistantQuestion
        question={
          currentGroup.duplicateQuestionData &&
          currentGroup.duplicateQuestionData?.amount > 1
            ? t(
                'ADVISORY-CONVERSATION_INPUT-COMPONENT_REUSE-ADDRESS_TITLE_MULTIPLE',
              )
            : t(
                'ADVISORY-CONVERSATION_INPUT-COMPONENT_REUSE-ADDRESS_TITLE_SINGLE',
                {
                  address: policyHolderAdress
                    ? formatAddress(policyHolderAdress)
                    : undefined,
                },
              )
        }
        {...(policyHolderAdress && {
          description: t(
            'ADVISORY-CONVERSATION_INPUT-COMPONENT_REUSE-ADDRESS_DESCRIPTION_ADDRESS',
            { address: formatAddress(policyHolderAdress) },
          ),
        })}
      />
      <QuestionGroupBody>
        {isLaptop && (
          <YesNoQuestion handleYes={handleYes} handleNo={handleNo} />
        )}
      </QuestionGroupBody>
      <QuestionGroupActions onBack={previousQuestionHandler} hideContinueButton>
        {!isLaptop && (
          <YesNoQuestion handleYes={handleYes} handleNo={handleNo} />
        )}
      </QuestionGroupActions>
    </AdvisoryFlowMainContainer>
  );
};

export default ReuseAddress;
